import {Component, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {DodBatchLayoutEndPoint} from '../../api/models/dod-batch-layout-end-point';
import {DodEndPointEx} from '../../api/models/dod-end-point-ex';
import {DodEndPointDimension} from '../../api/models/dod-end-point-dimension';
import {DodEndPointMeasureEx} from '../../api/models/dod-end-point-measure-ex';
import {DodEndPointColumn} from '../../api/models/dod-end-point-column';
import {DodEndPointAggregateType} from '../../api/models/dod-end-point-aggregate-type';
import {Observable} from 'rxjs';

export interface DodEndPointConfigDialogData {
  endPoint: DodBatchLayoutEndPoint;
  endPointEx: DodEndPointEx;
}

export interface AdditionalData {
  dodEndPointSubDataId: number;
  key: string;
  title: string;
  selected: boolean;
  outputFolderName: string;
  defaultOutputFolderName: string;
}

export interface DimensionSelection extends DodEndPointDimension {
  selected: boolean;
}

export interface MeasureSelection extends DodEndPointMeasureEx {
  selected: boolean;
  selectedAggregate: DodEndPointAggregateType;
}

export interface ColumnSelection extends DodEndPointColumn {
  selected: boolean;
}

@Component({
  selector: 'app-data-on-demand-end-point-config-dialog',
  templateUrl: './data-on-demand-end-point-config-dialog.component.html',
  styleUrls: ['./data-on-demand-end-point-config-dialog.component.css']
})
export class DataOnDemandEndPointConfigDialogComponent {
  public additionalData: AdditionalData[] = [];
  public dimensions: DimensionSelection[] = [];
  public measures: MeasureSelection[] = [];
  public columns: ColumnSelection[] = [];
  public groupedColumns: { group: string, columns: ColumnSelection[] }[] = [];

  constructor(
    public dialogRef: MatDialogRef<DataOnDemandEndPointConfigDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DodEndPointConfigDialogData
  ) {
    const subDataOutputFolders = this.data.endPoint.subDataOutputFolderName?.split(',').map(x => x.split(':')) ?? [];

    this.additionalData = this.data.endPointEx.subData?.map<AdditionalData>(x => {
      const savedFolder = subDataOutputFolders.find(y => y[0] === x.key);
      return {
        dodEndPointSubDataId: x.dodEndPointSubDataId,
        key: x.key,
        title: x.title,
        selected: this.data.endPoint.subDataSelected?.split(',').includes(x.key) ?? false,
        outputFolderName: savedFolder ? savedFolder[1] : (x.defaultOutputFolderName ?? ''),
        defaultOutputFolderName: x.defaultOutputFolderName
      };
    }) ?? [];

    this.dimensions = this.data.endPointEx.dimensions?.map<DimensionSelection>(x => ({
      ...x,
      selected: this.data.endPoint.dimensionsSelected?.split(',').includes(x.key) ?? false
    })) ?? [];

    const selectedMeasures = this.data.endPoint.measuresSelected?.split(',').map(x => x.split(':')) ?? [];
    this.measures = this.data.endPointEx.measures?.map<MeasureSelection>(x => {
      const selected = selectedMeasures.find(y => y[0] === x.key);
      return {
        ...x,
        selected: !!selected,
        selectedAggregate: selected ? (x.aggregates.find(y => y.key === selected[1]) ?? x.aggregates[0]) : x.aggregates[0]
      };
    }) ?? [];

    this.columns = this.data.endPointEx.columns?.map<ColumnSelection>(x => ({
      ...x,
      selected: this.data.endPoint.columnsSelected?.split(',').includes(x.columnSelector) ?? false
    })) ?? [];

    this.rebuildGroupedColumns();
  }

  public rebuildGroupedColumns() {
    this.groupedColumns = [];
    const selectedAdditionalData = this.additionalData.filter(x => x.selected).map(x => x.key);

    this.columns.forEach(column => {
      if (!column.dodEndPointSubDataId || selectedAdditionalData.includes(this.data.endPointEx.subData.find(s => s.dodEndPointSubDataId === column.dodEndPointSubDataId)?.key)) {
        let group = this.groupedColumns.find(x => x.group === column.group);
        if (!group) {
          group = { group: column.group, columns: [] };
          this.groupedColumns.push(group);
        }
        group.columns.push(column);
      }
    });
  }

  isGroupSelected(group: { group: string, columns: ColumnSelection[] }): boolean {
    return group.columns.every(x => x.selected);
  }

  isGroupIndeterminate(group: { group: string, columns: ColumnSelection[] }): boolean {
    const selectedCount = group.columns.filter(x => x.selected).length;
    return selectedCount > 0 && selectedCount < group.columns.length;
  }

  toggleGroupSelection(group: { group: string, columns: ColumnSelection[] }, event: any) {
    group.columns.forEach(x => x.selected = event.checked);
  }

  onSave() {
    this.data.endPoint.subDataSelected = this.additionalData.filter(x => x.selected).map(x => x.key).join(',');
    this.data.endPoint.subDataOutputFolderName = this.additionalData.filter(x => x.selected).map(x => x.key + ':' + x.outputFolderName).join(',');
    this.data.endPoint.dimensionsSelected = this.dimensions.filter(x => x.selected).map(x => x.key).join(',');
    this.data.endPoint.measuresSelected = this.measures.filter(x => x.selected).map(x => x.key + ':' + x.selectedAggregate.key).join(',');
    this.data.endPoint.columnsSelected = this.columns.filter(x => x.selected).map(x => x.columnSelector).join(',');
    this.dialogRef.close(this.data.endPoint);
  }

  onCancel() {
    this.dialogRef.close();
  }

  static showDialog(dialog: MatDialog, endPoint: DodBatchLayoutEndPoint, endPointEx: DodEndPointEx): Observable<DodBatchLayoutEndPoint> {
    const dialogRef = dialog.open(DataOnDemandEndPointConfigDialogComponent, {
      width: '800px',
      data: {
        endPoint: JSON.parse(JSON.stringify(endPoint)), // Clone to avoid direct modification
        endPointEx: endPointEx
      }
    });

    return dialogRef.afterClosed();
  }

  protected measureSelected(measure: MeasureSelection) {
    if (measure.selected) {
      measure.selectedAggregate = measure.aggregates[0];
    }
  }
}
